import { IMAGE_TYPES, IMAGE_TYPE_LABELS, LATERALITIES, LATERALITY_CODES, lateralityOfCode, type ImageType } from '../../../data/imageTypes';
import { viewableSeries } from '../../../data/derivedSeries';
import type { NavigatorImage } from './api';

/**
 * How the navigator lays a study out: one section per Type, in the fixed
 * order, "Other" last; inside each, one group per (specimen, site, laterality)
 * — by CODES, the same band the server places an image within — and inside a
 * group the order given: the saved one once somebody placed the study, else
 * by name (orderByName, which the page applies first). Each image is numbered by its
 * place in its GROUP as shown, 1…n afresh in every group (site and side,
 * within its Type; user, 2026-09-26, replacing the whole-study count of
 * 2026-09-24), so a drag renumbers the group ("the ordinal must change when I
 * reorder"). Pure, so it is tested as such.
 */

export interface NavigatorGroup {
  /** The band an image may be moved within; equal for two images exactly when the server would allow the move. */
  key: string;
  /** "A1 · Eye, Left", the parts that are known; "Unspecified" when none is. */
  title: string;
  images: NavigatorImage[];
}

export interface NavigatorSection {
  type: ImageType | null;
  label: string;
  count: number;
  groups: NavigatorGroup[];
}

/** Where an image goes: after one neighbour, or before one, of its own group. */
export interface Neighbours {
  afterId?: string;
  beforeId?: string;
}

/**
 * The image's specimen id, as the navigator sent it: never one of the
 * converter's stand-ins (catalog/navigator.ts leaves those out), and only to
 * enterprise members.
 */
export const specimenOf = (image: NavigatorImage): string | null => image.specimen_id || null;

/**
 * The band key of an image. `specimen` says whether the specimen tells
 * groups apart (enterprise): the community band never includes it.
 */
export function groupKey(image: NavigatorImage, specimen: boolean): string {
  return [image.type ?? '', specimen ? specimenOf(image) ?? '' : '', image.site?.code ?? '', image.laterality?.code ?? ''].join('|');
}

/** The image's name, when it has none: what kind of picture it is. The ordinal is shown apart. */
export const nameOf = (image: NavigatorImage): string => image.name ?? (image.kind === 'wsi' ? 'Slide' : image.kind === 'document' ? 'Document' : 'Image');

/** One locale for every viewer, so a hub, its sites and a share link sort alike. */
const natural = (a: string, b: string) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });

/** Known first, in natural order; none last. */
const compareOptional = (a: string | null, b: string | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : natural(a, b);

/** Left, Right, Bilateral, then none (or a code that is none of the three). */
const lateralityRank = (image: NavigatorImage): number => {
  const l = lateralityOfCode(image.laterality?.code);
  return l ? LATERALITIES.indexOf(l) : LATERALITIES.length;
};

/** The laterality's word, from the code (the one source), else as the server put it. */
const lateralityMeaning = (image: NavigatorImage): string | null => {
  const l = lateralityOfCode(image.laterality?.code);
  return l ? LATERALITY_CODES[l].meaning : image.laterality?.meaning ?? null;
};

/**
 * "Lung, Left": the site and its side as anatomic pathology names a specimen,
 * organ then laterality after a comma (the user, 2026-10-10); the site alone
 * without a side, the side alone without a site, null with neither.
 */
export const siteWithSide = (image: NavigatorImage): string | null =>
  [image.site?.meaning || null, lateralityMeaning(image)].filter((p): p is string => !!p).join(', ') || null;

/**
 * "5 of 7 · 2-5 · Ki67 · Lung, Left": how the header and the compare panes
 * name an image (the user, 2026-10-10). Its number out of its group's count —
 * the number its card shows, of the cards its group shows — then its name,
 * its stain unless the name already is it, and its site with its side. What is
 * not known is left out.
 */
export function displayName(image: NavigatorImage): string {
  const name = nameOf(image);
  const stain = (image.stain?.meaning ?? '').trim() || null;
  const sameAsName = stain !== null && stain.toLocaleLowerCase('en') === name.toLocaleLowerCase('en');
  return [`${image.ordinal} of ${image.of}`, name, sameAsName ? null : stain, siteWithSide(image)]
    .filter((p): p is string => !!p)
    .join(' · ');
}

function groupTitle(image: NavigatorImage, specimen: boolean): string {
  const parts = [specimen ? specimenOf(image) : null, siteWithSide(image)].filter((p): p is string => !!p);
  return parts.length ? parts.join(' · ') : 'Unspecified';
}

function compareGroups(a: NavigatorImage, b: NavigatorImage, specimen: boolean): number {
  return (
    (specimen ? compareOptional(specimenOf(a), specimenOf(b)) : 0) ||
    compareOptional(a.site?.meaning ?? null, b.site?.meaning ?? null) ||
    lateralityRank(a) - lateralityRank(b)
  );
}

/**
 * The images by name (decided 2026-09-30): the order a study nobody placed is
 * shown in. Natural order ("A1-2" before "A1-10"), case and accents alike,
 * equal names as they arrived, an image with no name last. The groups are
 * formed from the order given (buildSections keeps it), so sorting the whole
 * list sorts every group. `later` names the images that arrived after the
 * page opened its list: they go after the others — the list a reader is
 * working down does not reshuffle under them — by name among themselves,
 * and the next load sorts them in.
 */
export function orderByName(images: NavigatorImage[], later?: ReadonlySet<string>): NavigatorImage[] {
  const rank = (img: NavigatorImage) => (later?.has(img.image_id) ? 1 : 0);
  return images
    .map((img, at) => ({ img, at }))
    .sort((x, y) => rank(x.img) - rank(y.img) || compareOptional(x.img.name || null, y.img.name || null) || x.at - y.at)
    .map((x) => x.img);
}

export function buildSections(images: NavigatorImage[], specimen: boolean): NavigatorSection[] {
  const byType = new Map<ImageType | null, NavigatorImage[]>();
  for (const img of images) {
    const list = byType.get(img.type) ?? [];
    list.push(img);
    byType.set(img.type, list);
  }
  return [...IMAGE_TYPES, null]
    .filter((t) => byType.has(t))
    .map((type) => {
      const groups = new Map<string, NavigatorGroup>();
      for (const img of byType.get(type)!) {
        const key = groupKey(img, specimen);
        const g = groups.get(key) ?? { key, title: groupTitle(img, specimen), images: [] };
        g.images.push(img);
        groups.set(key, g);
      }
      const sorted = [...groups.values()].sort((a, b) => compareGroups(a.images[0], b.images[0], specimen));
      return {
        type,
        label: type ? IMAGE_TYPE_LABELS[type] : 'Other',
        count: byType.get(type)!.length,
        // Numbered afresh in each group, down it as shown, out of the group's count.
        groups: sorted.map((g) => ({ ...g, images: g.images.map((img, k) => ({ ...img, ordinal: k + 1, of: g.images.length })) })),
      };
    });
}

/** The images as the navigator shows them, top to bottom, each with its number. */
export const inDisplayOrder = (images: NavigatorImage[], specimen: boolean): NavigatorImage[] =>
  buildSections(images, specimen).flatMap((sec) => sec.groups.flatMap((g) => g.images));

/**
 * The images with one moved next to a neighbour: what the cache shows the
 * moment a drop is made, and what the list looks like while the pointer is
 * still over the drop. Neither neighbour named: the image stays where it is.
 */
export function reorder(images: NavigatorImage[], imageId: string, target: Neighbours): NavigatorImage[] {
  const moved = images.find((i) => i.image_id === imageId);
  const anchor = target.beforeId ?? target.afterId;
  if (!moved || !anchor || anchor === imageId) return images;
  const rest = images.filter((i) => i.image_id !== imageId);
  const at = rest.findIndex((i) => i.image_id === anchor);
  if (at < 0) return images;
  const out = rest.slice();
  out.splice(target.beforeId ? at : at + 1, 0, moved);
  return out;
}

/**
 * Prefix of the id a series gets when QIDO lists it and the navigator does
 * not: a moment after a publish, or where there is no navigator at all (the
 * enterprise lookup found no public id, /dev). Such an image has no
 * thumbnail by id and cannot be moved.
 */
export const TAIL_PREFIX = 'uid:';
export const isTail = (image: NavigatorImage): boolean => image.image_id.startsWith(TAIL_PREFIX);

/** How many of some images the reader has seen, of how many can be. */
export interface SeenTally {
  seen: number;
  total: number;
}

/**
 * Of the images that can carry a mark, how many the reader has seen: a
 * section's, or the whole study's. A series the navigator does not know (the
 * tail) has no mark to keep, so it is not counted either way.
 */
export function seenTally(images: NavigatorImage[]): SeenTally {
  let seen = 0;
  let total = 0;
  for (const img of images) {
    if (isTail(img)) continue;
    total++;
    if (img.seen_utc) seen++;
  }
  return { seen, total };
}

interface ListedSeries {
  seriesInstanceUID: string;
  modality: string;
  description: string;
}

/**
 * The navigator's images that QIDO lists (one it does not list cannot be
 * opened), followed by the series QIDO lists and the navigator does not, as
 * "Other" images, by name (numbered with that section by buildSections). What
 * an AI model derived from an image (a SEG or SR series) is never an image,
 * whoever lists it.
 */
export function withSeriesTail(images: NavigatorImage[], all: ListedSeries[]): NavigatorImage[] {
  const series = viewableSeries(all);
  const listed = new Set(series.map((s) => s.seriesInstanceUID));
  const known = new Set(images.map((i) => i.series_instance_uid));
  const tail = series.filter((s) => !known.has(s.seriesInstanceUID));
  return [
    ...images.filter((i) => listed.has(i.series_instance_uid)),
    ...orderByName(tail.map((s) => ({
      image_id: TAIL_PREFIX + s.seriesInstanceUID,
      series_instance_uid: s.seriesInstanceUID,
      kind: s.modality === 'SM' ? ('wsi' as const) : s.modality === 'DOC' ? ('document' as const) : ('flat' as const),
      type: null,
      name: s.description.trim() || null,
      stain: null,
      site: null,
      laterality: null,
      specimen_id: null,
      ordinal: 0,
      of: 0,
    }))),
  ];
}

/** The series in navigator order; the ones the navigator does not know keep their own order, at the end. */
export function sortSeries<S extends { seriesInstanceUID: string }>(series: S[], images: NavigatorImage[]): S[] {
  const rank = new Map(images.map((img, i) => [img.series_instance_uid, i]));
  return series
    .map((s, i) => ({ s, r: rank.get(s.seriesInstanceUID) ?? images.length + i }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.s);
}
